<div class="flex flex-col gap-4 p-4">
  <!-- Regulatory dashboard with expiring-compliances widget -->
  <!-- Header -->
  <div class="flex items-center justify-between">
    <div>
      <h1 class="text-default text-xl font-bold">Regulatory Dashboard</h1>
      <p class="text-secondary text-sm">Compliance, document status, integration health and upcoming deadlines</p>
    </div>
  </div>

  <!-- Filter Bar -->
  <div class="rounded-xl border bg-white px-4 py-1">
    <app-global-filter-panel [config]="filterConfig" (filtersChanged)="onFiltersChanged($event)" />
  </div>

  <!-- Compliance KPIs — scoped to the company (trade license + vendors) -->
  <app-kpi-card-grid [cards]="complianceKpis" title="COMPANY COMPLIANCE OVERVIEW" [columns]="5" />

  <!-- Compliances Expiring Soon (30 days) — risk meter + items + link to tab -->
  <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
    <div class="mb-3 flex items-center justify-between gap-3">
      <div>
        <h3 class="text-default text-sm font-bold">Compliances Expiring Soon</h3>
        <p class="text-secondary text-xs">Items due in the next 30 days across all scopes (company, community, unit, vendor)</p>
      </div>
      <a routerLink="/compliances/expiring" class="text-xs font-medium text-blue-600 hover:underline">View all →</a>
    </div>

    @if (!hasCompliancePermission) {
      <div class="flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 p-6 text-sm font-semibold text-red-700">
        <mat-icon class="!text-base text-red-400">lock</mat-icon>
        Permissions Restricted
      </div>
    } @else if (loadingExpiring) {
      <div class="flex justify-center py-6"><mat-spinner diameter="28" /></div>
    } @else if (expiringComplianceData) {
      <!-- Risk meter progress bar -->
      <div class="mb-4">
        <div class="mb-1 flex items-center justify-between text-xs">
          <span class="text-secondary font-semibold uppercase tracking-wide">Expiry Risk</span>
          <span class="text-xs font-bold" [ngClass]="getRiskTextClass(expiringComplianceData.risk_percent ?? 0)">
            {{ expiringComplianceData.risk_percent ?? 0 }}%
            <span class="text-secondary ml-1 font-normal"> ({{ expiringComplianceData.expiring_count ?? 0 }} of {{ expiringComplianceData.total ?? 0 }}) </span>
          </span>
        </div>
        <div class="h-2 w-full overflow-hidden rounded-full bg-gray-100">
          <div
            class="h-full rounded-full transition-all duration-500"
            [ngClass]="getRiskBarColor(expiringComplianceData.risk_percent ?? 0)"
            [style.width.%]="expiringComplianceData.risk_percent ?? 0"
          ></div>
        </div>
      </div>

      <!-- 4 bucket counts -->
      <div class="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <div class="rounded-lg border border-green-100 bg-green-50 p-2 text-center">
          <div class="text-xl font-bold text-green-700">{{ expiringComplianceData.healthy_count ?? 0 }}</div>
          <div class="text-secondary mt-0.5 text-[10px] font-semibold uppercase tracking-wide">Healthy</div>
        </div>
        <div class="rounded-lg border border-amber-100 bg-amber-50 p-2 text-center">
          <div class="text-xl font-bold text-amber-700">{{ expiringComplianceData.expiring_count ?? 0 }}</div>
          <div class="text-secondary mt-0.5 text-[10px] font-semibold uppercase tracking-wide">Expiring 30d</div>
        </div>
        <div class="rounded-lg border border-red-100 bg-red-50 p-2 text-center">
          <div class="text-xl font-bold text-red-700">{{ expiringComplianceData.expired_count ?? 0 }}</div>
          <div class="text-secondary mt-0.5 text-[10px] font-semibold uppercase tracking-wide">Expired</div>
        </div>
        <div class="rounded-lg border border-gray-100 bg-gray-50 p-2 text-center">
          <div class="text-xl font-bold text-gray-700">{{ expiringComplianceData.missing_docs ?? 0 }}</div>
          <div class="text-secondary mt-0.5 text-[10px] font-semibold uppercase tracking-wide">Missing Docs</div>
        </div>
      </div>

      <!-- Top expiring items list — work-queue card style -->
      @if (expiringComplianceData.items?.length > 0) {
        <div class="flex flex-col gap-1">
          @for (it of expiringComplianceData.items.slice(0, 5); track it.id) {
            <div
              class="flex items-center gap-2.5 overflow-hidden rounded-lg border border-l-4 border-gray-100 bg-white py-2 pl-2.5 pr-2.5 shadow-sm"
              [class.border-l-red-500]="it.days_left <= 7"
              [class.border-l-orange-400]="it.days_left > 7 && it.days_left <= 14"
              [class.border-l-amber-400]="it.days_left > 14"
            >
              <!-- Scope icon badge -->
              <div class="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-gray-100">
                <mat-icon class="!text-sm !leading-none text-gray-500">{{ getScopeIcon(it.scope) }}</mat-icon>
              </div>
              <!-- Content -->
              <div class="min-w-0 flex-1">
                <p class="line-clamp-1 text-xs font-semibold leading-snug text-gray-800">{{ it.label_name }}</p>
                <div class="mt-0.5 flex flex-wrap items-center gap-x-1 text-[10px] text-gray-400">
                  <span>{{ getScopeLabel(it.scope) }}: {{ it.entity_name }}</span>
                  @if (it.parent_name) {
                    <span class="text-gray-300">·</span>
                    <span>{{ it.parent_name }}</span>
                  }
                  <span class="text-gray-300">·</span>
                  <span>{{ it.expiry | date: 'mediumDate' }}</span>
                </div>
              </div>
              <!-- Days left badge -->
              <span
                class="flex-shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider"
                [class.bg-red-100]="it.days_left <= 7"
                [class.text-red-700]="it.days_left <= 7"
                [class.bg-orange-100]="it.days_left > 7 && it.days_left <= 14"
                [class.text-orange-700]="it.days_left > 7 && it.days_left <= 14"
                [class.bg-amber-100]="it.days_left > 14"
                [class.text-amber-700]="it.days_left > 14"
                >{{ it.days_left }}d left</span
              >
            </div>
          }
        </div>
      } @else {
        <div class="flex flex-col items-center gap-1.5 rounded-lg border border-dashed border-gray-200 py-8 text-center">
          <mat-icon class="!text-3xl text-gray-300">verified_user</mat-icon>
          <span class="text-xs text-gray-400">No compliances expiring in the next 30 days</span>
        </div>
      }
    }
  </div>

  <!-- Document Compliance + Contract Expiry Tracker + Regulatory Authority Status -->
  <div class="grid grid-cols-1 gap-4 lg:grid-cols-3">
    <!-- Document Compliance -->
    <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <div class="mb-3 flex items-center gap-2">
        <div class="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-50">
          <mat-icon class="!text-sm text-blue-500">description</mat-icon>
        </div>
        <h3 class="text-default text-sm font-bold">Document Compliance</h3>
      </div>
      @if (!hasCompliancePermission) {
        <div class="flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 p-6 text-sm font-semibold text-red-700">
          <mat-icon class="!text-base text-red-400">lock</mat-icon>
          Permissions Restricted
        </div>
      } @else if (loadingDocument) {
        <div class="flex justify-center py-6"><mat-spinner diameter="28" /></div>
      } @else if (documentData) {
        <p class="text-secondary mb-3 text-xs">Tenancy contract expiry status across all units</p>
        <div class="flex flex-col gap-2">
          <!-- Expiring soon -->
          <div class="flex items-center gap-3 rounded-lg border border-l-4 border-gray-100 border-l-orange-400 bg-white px-3 py-2.5 shadow-sm">
            <div class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-orange-50">
              <mat-icon class="!text-sm text-orange-500">schedule</mat-icon>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-xs font-semibold text-gray-800">Expiring Soon</p>
              <p class="text-[10px] text-gray-400">Within the next 90 days</p>
            </div>
            <span class="rounded-full bg-orange-100 px-2 py-0.5 text-sm font-bold text-orange-700">{{ documentData.ejari_expiring_90d ?? 0 }}</span>
          </div>
          <!-- Expired -->
          <div class="flex items-center gap-3 rounded-lg border border-l-4 border-gray-100 border-l-red-500 bg-white px-3 py-2.5 shadow-sm">
            <div class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-red-50">
              <mat-icon class="!text-sm text-red-500">event_busy</mat-icon>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-xs font-semibold text-gray-800">Expired</p>
              <p class="text-[10px] text-gray-400">Tenancy contract past due</p>
            </div>
            <span class="rounded-full bg-red-100 px-2 py-0.5 text-sm font-bold text-red-700">{{ documentData.ejari_expired ?? 0 }}</span>
          </div>
        </div>
      }
    </div>

    <!-- Contract Expiry Tracker -->
    <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <app-contract-expiry-tracker [dashboardType]="dashboardType" [filters]="filters" [requiredPermission]="'rfp_view'" />
    </div>

    <!-- Regulatory Authority Status -->
    <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <h3 class="text-secondary mb-3 text-xs font-semibold uppercase tracking-wide">Regulatory Authority Status</h3>
      @if (!hasCompliancePermission) {
        <div class="flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 p-6 text-sm font-semibold text-red-700">
          <mat-icon class="!text-base text-red-400">lock</mat-icon>
          Permissions Restricted
        </div>
      } @else if (loadingAuthority) {
        <div class="flex justify-center py-6"><mat-spinner diameter="28" /></div>
      } @else if (authorityData?.items?.length > 0) {
        <div class="space-y-2">
          @for (it of authorityData.items; track it.key) {
            <div class="flex items-center gap-3 rounded-lg border p-2.5" [ngClass]="getAuthorityRowClass(it.status)">
              <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg" [ngClass]="getAuthorityIconBg(it.status)">
                <mat-icon class="!h-5 !w-5 !text-[18px] !leading-none" [ngClass]="getAuthorityIconColor(it.status)">{{ it.icon }}</mat-icon>
              </div>
              <div class="min-w-0 flex-1">
                <p class="text-default truncate text-sm font-semibold">{{ it.label }}</p>
                <p class="text-secondary truncate text-xs">{{ it.detail }}</p>
              </div>
              <span class="shrink-0 rounded-full px-2.5 py-1 text-xs font-bold" [ngClass]="getAuthorityBadgeClass(it.status)">
                {{ it.value }}
              </span>
            </div>
          }
        </div>
      } @else if (!loadingAuthority) {
        <p class="text-secondary py-4 text-center text-sm">No authority data available</p>
      }
    </div>
  </div>

  <!-- Bottom Row: Work Queue + Timeline -->
  <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
    <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <app-work-queue-list [filters]="filters" title="My Work Queue" />
    </div>
    <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <app-activity-timeline [requiredPermission]="'activity_logs_other'" />
    </div>
  </div>
</div>
